import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { PropertyLocationModalProps } from '../../types/property-location-fields.type';
import type { PropertyFormInputValues } from '../../validations/property-validations';
import { PropertyLocationEditor } from './property-location-editor';

/** Keeps modal changes isolated until explicitly applied to the property form. */
export const PropertyLocationModal: React.FC<PropertyLocationModalProps> = ({
  initialValues,
  tenantCountryCode,
  disabled,
  onClose,
  onApply,
}) => {
  const { t } = useI18n();
  const { register, setValue, watch } = useForm<PropertyFormInputValues>({
    defaultValues: initialValues,
  });
  const values = watch();
  const [pending, setPending] = useState(false);
  return (
    <Modal
      isOpen
      onClose={onClose}
      ariaLabel={t('catalog.properties.location.edit')}
      dialogClassName="sm:!max-w-5xl sm:!max-h-[90dvh]"
      bodyClassName="!p-5"
      headerClassName="!px-5 !py-4"
      header={
        <div className="space-y-1">
          <Text size="h6" weight="semibold">
            {t('catalog.properties.location.edit')}
          </Text>
          <Text size="small" className="text-slate-500">
            {t('catalog.properties.location.autocomplete.description')}
          </Text>
        </div>
      }
      body={
        <form
          className="w-full"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <PropertyLocationEditor
            register={register}
            setValue={setValue}
            values={values}
            tenantCountryCode={tenantCountryCode}
            disabled={disabled}
            onPendingChange={setPending}
          />
        </form>
      }
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
          <Text size="tiny" className="text-slate-500">
            {t('catalog.properties.location.saveWithProperty')}
          </Text>
          <div className="ml-auto flex gap-2">
            <Button
              type="button"
              variant="neutral"
              appearance="ghost"
              onClick={onClose}
            >
              {t('catalog.products.form.cancel')}
            </Button>
            <Button
              type="button"
              disabled={disabled || pending}
              onClick={() => onApply(values)}
            >
              {t('catalog.properties.location.apply')}
            </Button>
          </div>
        </div>
      }
    />
  );
};
